import { useCallback, useEffect, useRef, useState } from "react";
import {
  useVoicemailMetadata,
  VoicemailMetadataStatus,
} from "./VoicemailMetadata";
import {
  DepartmentVoicemailPanel,
  parsePrivateVoicemailAudio,
  releaseVoicemailAudio,
  parseVoicemailMessages,
  voicemailRecord,
  voicemailText,
  voicemailInbox,
  type VoicemailFilter,
} from "./DepartmentVoicemailPanel";

type Api = <T>(
  path: string,
  options?: {
    method?: string;
    body?: unknown;
    csrf?: string | undefined;
    signal?: AbortSignal;
  },
) => Promise<T>;
type Message = {
  id: string;
  folder: "INBOX" | "Old";
  receivedAt: string | null;
  durationSeconds: number | null;
};
type PhonePrivateIdentity = {
  client: Api;
  scope: string;
  lineId: string;
  deviceId: string;
  connectionVersion: number;
};
export type Mailbox = {
  lineId: string;
  deviceId: string;
  connectionVersion: number;
  label: string;
  extension: string | null;
  enabled: boolean;
  pinConfigured: boolean;
  newMessages: number;
  oldMessages: number;
  messages: Message[];
};

export function parsePhoneMailboxes(value: unknown): { items: Mailbox[] } {
  if (
    !voicemailRecord(value) ||
    Object.keys(value).length !== 1 ||
    !Array.isArray(value.items) ||
    value.items.length > 1000
  )
    throw new Error(
      "The voicemail response was incomplete. Refresh before trying again.",
    );
  const ids = new Set<string>(),
    uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const items = value.items.map((box: unknown) => {
    if (
      !voicemailRecord(box) ||
      Object.keys(box).length !== 10 ||
      typeof box.lineId !== "string" ||
      !uuid.test(box.lineId) ||
      ids.has(box.lineId) ||
      typeof box.deviceId !== "string" ||
      !uuid.test(box.deviceId) ||
      !Number.isSafeInteger(box.connectionVersion) ||
      Number(box.connectionVersion) < 1 ||
      !voicemailText(box.label, 200) ||
      !(
        box.extension === null ||
        (typeof box.extension === "string" &&
          /^[0-9]{1,16}$/.test(box.extension))
      ) ||
      typeof box.enabled !== "boolean" ||
      typeof box.pinConfigured !== "boolean" ||
      !Number.isSafeInteger(box.newMessages) ||
      Number(box.newMessages) < 0 ||
      Number(box.newMessages) > 10000 ||
      !Number.isSafeInteger(box.oldMessages) ||
      Number(box.oldMessages) < 0 ||
      Number(box.oldMessages) > 10000
    )
      throw new Error(
        "The voicemail response was incomplete. Refresh before trying again.",
      );
    ids.add(box.lineId);
    return {
      ...box,
      messages: parseVoicemailMessages(box.messages),
    } as Mailbox;
  });
  return { items };
}

export function MailboxSummary({ box }: { box: Mailbox }) {
  return (
    <>
      <h4>
        {box.label}
        {box.extension ? ` · Extension ${box.extension}` : ""}
      </h4>
      <p>
        {box.enabled
          ? "Voicemail is enabled on the phone server."
          : "Voicemail is off. Enable it in Features, save and apply call handling."}
      </p>
      <p>
        {box.newMessages} new · {box.oldMessages} saved ·{" "}
        {box.newMessages + box.oldMessages} total
      </p>
      <p>
        Dial *97 from this line and enter its PIN to listen, save or delete
        messages. Forwarding takes priority; Do Not Disturb returns busy. New
        and Saved are folders on the phone server. Listening here does not mark
        a message as saved.
      </p>
    </>
  );
}

export function VoicemailPanel({
  api,
  accountId,
  customer,
  csrf,
  readOnly = false,
  refreshVersion = 0,
}: {
  api: Api;
  accountId: string;
  customer: boolean;
  csrf: string | undefined;
  readOnly?: boolean;
  refreshVersion?: number;
}) {
  const base = `/${customer ? "customer" : "admin"}/accounts/${accountId}/voicemail`;
  const [selected, setSelected] = useState("");
  const [refresh, setRefresh] = useState(0),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState<VoicemailFilter>("new");
  const [pin, setPin] = useState<
      (PhonePrivateIdentity & { value: string }) | null
    >(null),
    [audio, setAudio] = useState<
      (PhonePrivateIdentity & { messageId: string; base64: string }) | null
    >(null);
  const working = useRef(false),
    generation = useRef(0);
  const mailboxChoice = useRef<{ scope: string; lineId: string } | null>(null);
  const request = useRef<AbortController | null>(null);
  const audioElement = useRef<HTMLAudioElement | null>(null);
  const bindAudio = useCallback((node: HTMLAudioElement | null) => {
    if (audioElement.current && audioElement.current !== node)
      releaseVoicemailAudio(audioElement.current);
    audioElement.current = node;
  }, []);
  useEffect(() => setFilter("new"), [base]);
  useEffect(() => {
    const hide = () => {
      ++generation.current;
      request.current?.abort();
      request.current = null;
      working.current = false;
      setBusy(false);
      setPin(null);
      setAudio(null);
    };
    window.addEventListener("blur", hide);
    window.addEventListener("pagehide", hide);
    document.addEventListener("visibilitychange", hide);
    return () => {
      hide();
      window.removeEventListener("blur", hide);
      window.removeEventListener("pagehide", hide);
      document.removeEventListener("visibilitychange", hide);
    };
  }, []);
  useEffect(() => {
    if (pin) {
      const timer = setTimeout(() => setPin(null), 30_000);
      return () => clearTimeout(timer);
    }
  }, [pin]);
  const metadata = useVoicemailMetadata(
    api,
    base,
    parsePhoneMailboxes,
    refresh,
    readOnly,
    refreshVersion,
  );
  useEffect(() => {
    setSelected("");
  }, [base]);
  useEffect(() => {
    ++generation.current;
    request.current?.abort();
    working.current = false;
    setBusy(false);
    setPin(null);
    setAudio(null);
    setError("");
    return () => {
      request.current?.abort();
      ++generation.current;
    };
  }, [api, base, refresh, readOnly]);
  useEffect(() => {
    if (!metadata.data) return;
    const items = metadata.data.items;
    const deliberate =
      mailboxChoice.current?.scope === base
        ? items.find((box) => box.lineId === mailboxChoice.current?.lineId)
        : undefined;
    setSelected(
      (previous) =>
        deliberate?.lineId ??
        items.find((box) => box.lineId === previous)?.lineId ??
        items.find((box) => box.newMessages > 0)?.lineId ??
        items[0]?.lineId ??
        "",
    );
  }, [base, metadata.data]);
  const currentState = metadata.data;
  const box = currentState?.items.find((item) => item.lineId === selected);
  const matchesPrivateBox = (value: PhonePrivateIdentity | null) =>
    value?.client === api &&
    value.scope === base &&
    value.lineId === box?.lineId &&
    value.deviceId === box?.deviceId &&
    value.connectionVersion === box?.connectionVersion;
  useEffect(() => {
    ++generation.current;
    request.current?.abort();
    request.current = null;
    working.current = false;
    setBusy(false);
    setPin(null);
    setAudio(null);
    setError("");
  }, [box?.lineId, box?.deviceId, box?.connectionVersion]);
  const phoneCounts = currentState?.items.reduce(
    (counts, item) => ({
      newMessages: counts.newMessages + item.newMessages,
      savedMessages: counts.savedMessages + item.oldMessages,
    }),
    { newMessages: 0, savedMessages: 0 },
  );
  const messages = box ? voicemailInbox(box.messages, filter) : [];
  async function open(action: "pin/reveal" | "audio", messageId?: string) {
    if (!box || working.current || readOnly) return;
    working.current = true;
    setBusy(true);
    setError("");
    setPin(null);
    setAudio(null);
    const identity: PhonePrivateIdentity = {
      client: api,
      scope: base,
      lineId: box.lineId,
      deviceId: box.deviceId,
      connectionVersion: box.connectionVersion,
    };
    const current = ++generation.current;
    const abort = new AbortController();
    request.current = abort;
    const timeout = setTimeout(() => {
      abort.abort();
      if (generation.current === current) {
        setError("The private voicemail request timed out. Try again.");
        request.current = null;
        working.current = false;
        setBusy(false);
      }
    }, 18_000);
    try {
      const result = await api<unknown>(`${base}/${action}`, {
        method: "POST",
        csrf,
        signal: abort.signal,
        body: {
          requestKey: crypto.randomUUID(),
          lineId: box.lineId,
          expectedConnectionVersion: box.connectionVersion,
          confirmed: true,
          ...(messageId ? { messageId } : {}),
        },
      });
      if (generation.current !== current || abort.signal.aborted) return;
      if (action === "pin/reveal") {
        if (
          !voicemailRecord(result) ||
          result.lineId !== box.lineId ||
          result.retrievalCode !== "*97" ||
          typeof result.pin !== "string" ||
          !/^[0-9]{8,79}$/.test(result.pin)
        )
          throw new Error(
            "The voicemail response was incomplete. Refresh before trying again.",
          );
        setPin({ ...identity, value: result.pin });
      } else if (messageId)
        setAudio({
          ...identity,
          messageId,
          base64: parsePrivateVoicemailAudio(result, {
            lineId: box.lineId,
            messageId,
          }),
        });
    } catch (cause) {
      if (generation.current === current)
        setError(
          abort.signal.aborted
            ? "The private voicemail request timed out. Try again."
            : (cause as Error).message,
        );
    } finally {
      clearTimeout(timeout);
      if (request.current === abort) {
        request.current = null;
        working.current = false;
        setBusy(false);
      }
    }
  }
  return (
    <section className="cr-panel" aria-label="Voicemail">
      <h2>Voicemail</h2>
      <DepartmentVoicemailPanel
        key={`${base}:department:${readOnly}`}
        api={api}
        accountId={accountId}
        customer={customer}
        csrf={csrf}
        readOnly={readOnly}
        refreshVersion={refreshVersion}
      />
      <h3>Individual phone mailboxes</h3>
      <VoicemailMetadataStatus
        checkedAt={metadata.checkedAt}
        loading={metadata.loading}
        error={metadata.error}
      />
      {error && (
        <p className="cr-notice error" role="alert">
          {error}
        </p>
      )}
      {!currentState && metadata.loading && (
        <p role="status">Loading phone voicemail…</p>
      )}
      {currentState?.items.length === 0 && (
        <p>
          No individual phone mailbox is installed for this account. Department
          messages, when configured, appear above. For phone voicemail, install
          a phone and enable voicemail in Features.
        </p>
      )}
      {currentState && currentState.items.length > 0 && (
        <>
          <p className="cr-mailbox-overview" role="status">
            {currentState.items.length} phone mailbox
            {currentState.items.length === 1 ? "" : "es"} ·{" "}
            <strong>{phoneCounts?.newMessages} new</strong> ·{" "}
            {phoneCounts?.savedMessages} saved
          </p>
          <label className="cr-field">
            <span>Phone or extension</span>
            <select
              value={selected}
              disabled={busy}
              onChange={(event) => {
                ++generation.current;
                setSelected(event.target.value);
                mailboxChoice.current = {
                  scope: base,
                  lineId: event.target.value,
                };
                setPin(null);
                setAudio(null);
                setError("");
              }}
            >
              {currentState.items.map((item) => (
                <option key={item.lineId} value={item.lineId}>
                  {item.label}
                  {item.extension ? ` · Ext. ${item.extension}` : ""}
                  {` · ${item.newMessages} new`}
                </option>
              ))}
            </select>
          </label>
        </>
      )}
      {box && (
        <>
          <MailboxSummary box={box} />
          {box.newMessages === 0 && Boolean(phoneCounts?.newMessages) && (
            <button
              className="cr-text-button"
              onClick={() => {
                const next = currentState?.items.find(
                  (item) => item.newMessages > 0,
                );
                if (!next) return;
                ++generation.current;
                request.current?.abort();
                working.current = false;
                setBusy(false);
                setPin(null);
                setAudio(null);
                setError("");
                mailboxChoice.current = { scope: base, lineId: next.lineId };
                setSelected(next.lineId);
              }}
            >
              Open a mailbox with new messages
            </button>
          )}
          <label className="cr-field">
            <span>Show phone messages</span>
            <select
              value={filter}
              onChange={(event) => {
                ++generation.current;
                request.current?.abort();
                request.current = null;
                working.current = false;
                setBusy(false);
                setPin(null);
                setAudio(null);
                setError("");
                setFilter(event.target.value as VoicemailFilter);
              }}
            >
              <option value="new">New ({box.newMessages})</option>
              <option value="saved">Saved ({box.oldMessages})</option>
              <option value="all">
                All ({box.newMessages + box.oldMessages})
              </option>
            </select>
          </label>
          {readOnly ? (
            <p>
              Read-only customer view. Exit to open private messages or reveal a
              PIN as staff.
            </p>
          ) : (
            <>
              <button
                type="button"
                className="cr-button secondary"
                disabled={busy || !box.pinConfigured}
                onClick={() => void open("pin/reveal")}
              >
                Show voicemail PIN
              </button>
              {pin && matchesPrivateBox(pin) && (
                <div className="cr-notice" role="status">
                  <p>
                    Voicemail PIN: <code>{pin.value}</code>
                  </p>
                  <button
                    className="cr-text-button"
                    onClick={() => setPin(null)}
                  >
                    Hide PIN
                  </button>
                  <p>
                    Hidden automatically after 30 seconds or when you leave this
                    window.
                  </p>
                </div>
              )}
            </>
          )}
          {!box.messages.length && <p>No messages recorded.</p>}
          {box.messages.length > 0 && messages.length === 0 && (
            <p>
              No {filter === "new" ? "new" : "saved"} messages in this phone
              mailbox. Choose another folder above.
            </p>
          )}
          <ul className="cr-message-list">
            {messages.map((item) => (
              <li key={item.id}>
                <p>
                  <strong>
                    {item.folder === "INBOX" ? "New message" : "Saved message"}
                  </strong>
                  {item.receivedAt
                    ? ` · ${new Date(item.receivedAt).toLocaleString()}`
                    : " · Time unavailable"}
                  {item.durationSeconds !== null
                    ? ` · ${Math.round(item.durationSeconds)} seconds`
                    : ""}
                </p>
                {!readOnly && (
                  <button
                    type="button"
                    className="cr-button secondary"
                    disabled={busy}
                    onClick={() => void open("audio", item.id)}
                  >
                    Listen to message
                  </button>
                )}
                {!readOnly &&
                  audio &&
                  matchesPrivateBox(audio) &&
                  audio.messageId === item.id && (
                    <div>
                      <audio
                        ref={bindAudio}
                        className="cr-audio"
                        controls
                        preload="none"
                        src={`data:audio/wav;base64,${audio.base64}`}
                        aria-label="Voicemail recording"
                      />
                      <button
                        type="button"
                        className="cr-text-button"
                        onClick={() => setAudio(null)}
                      >
                        Close recording
                      </button>
                    </div>
                  )}
              </li>
            ))}
          </ul>
        </>
      )}
      <button
        className="cr-button secondary"
        type="button"
        disabled={busy}
        onClick={() => setRefresh((value) => value + 1)}
      >
        Refresh phone voicemail
      </button>
    </section>
  );
}
